import { useEffect, useMemo, type ReactNode } from "react";
import { useLocation } from "react-router";
import Loading from "@core/layouts/loading/Loading";
import LoginAlert from "@features/auth/component/authGuardDialog/LoginAlert";
import JoinAlert from "@features/auth/component/authGuardDialog/JoinAlert";
import WithdrawnAlert from "@features/auth/component/authGuardDialog/WithdrawnAlert";
import LayerAccountRegister from "@features/auth/component/authGuardDialog/LayerAccountRegister";
import LayerAccountChange from "@features/auth/component/authGuardDialog/LayerAccountChange";
import LayerLoginPlayncAccount from "@features/auth/component/authGuardDialog/LayerLoginPlayncAccount";
import { useAuthStore } from "@core/stores/authStore";
import { useAccountStore } from "@core/stores/accountStore";
import { useFetchLoginInfo } from "@features/auth/hooks/queries/useLogin";
import { shallow } from "zustand/shallow";
import type { AuthType } from "@core/types/authType";

interface IAuthGuardProps {
  children: ReactNode;
  requiredAuth: (AuthType | "PUBLIC" | "PLAYNC_USER_2_ALL")[]; // PUBLIC: 모든 권한 접근 허용, PLAYNC_USER_2_ALL: PLAYNC_USER_2 권한 중 대표 PC방 O, X 전부 접근 허용하는 값 추가
}

/**
 * 권한 처리 케이스
 * 1. 로그아웃: 로그인 레이어 노출
 * 2. NCFZ 기존 계정으로 로그인 및 Plaync로 전환 전일 때(login-info.ncfzUserStatusCode: NCFZ_USER_0일 때): 회원 전환 레이어 노출
 * 3. NCFZ 기존 계정으로 로그인 및 Plaync로 전환한 계정일 때(login-info.ncfzUserStatusCode: NCFZ_USER_1일 때): PLAYNC 로그인 레이어 노출
 * 4. PLAYNC 로그인 및 등록된 PC방이 없는 상태(신규 ncfz 미가입) / 탈퇴 완료된 상태(login-info.ncfzUserStatusCode: PLAYNC_USER_0): 회원 가입 레이어 노출
 * 5. PLAYNC 로그인 및 가입 승인 미완료(login-info.ncfzUserStatusCode: PLAYNC_USER_1): 가입 승인 후 확인하라는 레이어 노출
 * 6. PLAYNC 로그인 및 대표PC방 있음(login-info.ncfzUserStatusCode: PLAYNC_USER_2): 전부 이동 가능
 * 7. 탈퇴 신청한 상태(아직 관리자 승인 전, 대표PC방 없음, PC방 전체 삭제)(login-info.ncfzUserStatusCode: PLAYNC_USER_2 && plaync-mapping(useICafeList)의 isWithdrawn: true): 사업장 삭제 레이어 노출
 */
export default function AuthGuard({ children, requiredAuth }: IAuthGuardProps) {
  const { data: loginInfo, isLoading: isLoginLoading } = useFetchLoginInfo();

  const setAccount = useAccountStore((state) => state.setAccount);
  const { auth, isLogin, isWithdrawn, setIsLogin, setIsLoginLoading, setAuth, setPrimaryICafeId, setPlayncICafeList, setIsWithdrawn, setLoginICafeInfo } = useAuthStore(
    (state) => ({
      auth: state.auth,
      isLogin: state.isLogin,
      isWithdrawn: state.isWithdrawn,
      setIsLogin: state.setIsLogin,
      setIsLoginLoading: state.setIsLoginLoading,
      setAuth: state.setAuth,
      setPrimaryICafeId: state.setPrimaryICafeId,
      setPlayncICafeList: state.setPlayncICafeList,
      setIsWithdrawn: state.setIsWithdrawn,
      setLoginICafeInfo: state.setLoginICafeInfo,
    }),
    shallow,
  );

  // 로그인 정보 조회
  useEffect(() => {
    setIsLogin(loginInfo?.isLogin ?? false);
    setAccount(loginInfo?.account ?? null);
    setAuth((loginInfo?.account?.ncfzUserStatusCode as AuthType) ?? null);
  }, [loginInfo, setIsLogin, setAccount, setAuth]);

  useEffect(() => {
    setIsLoginLoading(isLoginLoading); // 로그인 정보 로딩 여부 (로그인 로딩 완료 후 권한 처리하도록)
  }, [isLoginLoading, setIsLoginLoading]);

  useEffect(() => {
    if (isLoginLoading && !isLogin) {
      setPrimaryICafeId(null);
      setPlayncICafeList(null);
      setIsWithdrawn(false);
    }
  }, [isLoginLoading, isLogin]);

  const location = useLocation();
  const isMainPage = useMemo(() => location.pathname === "/", [location.pathname]);
  let component = null;

  // 로그인 정보 로딩 중
  if (isLoginLoading || !auth) {
    component = <Loading />;
  }

  if (!requiredAuth.includes("PUBLIC")) {
    // 로그아웃일 때: 로그인 레이어 노출
    if (auth === "LOGOUT_USER" && !requiredAuth.includes(auth)) {
      component = <LoginAlert />;
    }

    // NCFZ 기존 계정으로 로그인 및 Plaync로 전환 전일 때: 회원 전환 레이어 노출
    if (auth === "NCFZ_USER_0" && !requiredAuth.includes(auth)) {
      component = <LayerAccountChange />;
    }

    // NCFZ 기존 계정으로 로그인 및 Plaync로 전환한 계정일 때: PLAYNC 로그인 레이어 노출
    if (auth === "NCFZ_USER_1" && !requiredAuth.includes(auth)) {
      component = <LayerLoginPlayncAccount />;
    }

    // PLAYNC 로그인 및 등록된 PC방이 없을 때(신규 ncfz 미가입) / 탈퇴 완료된 상태: 회원 가입 레이어 노출
    if (auth === "PLAYNC_USER_0" && !requiredAuth.includes(auth)) {
      component = <LayerAccountRegister />;
    }

    // PLAYNC 로그인 및 가입 승인 미완료: 가입 승인 후 확인하라는 레이어 노출
    if (auth === "PLAYNC_USER_1" && !requiredAuth.includes(auth)) {
      component = <JoinAlert />;
    }

    // 탈퇴 신청한 상태(아직 관리자 승인 전, 대표PC방 없음, PC방 전체 삭제): 사업장 삭제 레이어 노출
    if (auth === "PLAYNC_USER_2" && isWithdrawn && !requiredAuth.includes("PLAYNC_USER_2_ALL")) {
      component = <WithdrawnAlert />;
    }
  }

  // (접근 가능) 별도 얼럿 메시지 없이 해당 페이지로 진입 함
  return isMainPage ? (
    <>
      {component}
      {children}
    </>
  ) : (
    component || children
  );
}
